import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, ArrowLeft, Cookie } from 'lucide-react';
import axios from 'axios';
import DOMPurify from 'dompurify';
import Footer from '../components/Footer';
import CookiePreferences from '../components/CookiePreferences';

import { BACKEND_URL } from '../config/api';

const defaultCookieContent = `
<p class="lead">Last updated: January 2026</p>

<p>This Cookie Policy explains how ONSITEHAND LIMITED ("we", "us", "our"), the operator of OnSiteHand, uses cookies and similar technologies when you visit our website and use our platform. ONSITEHAND LIMITED is a private limited company registered in England and Wales with company number 17186065, registered office Unit A449, 4-6 Greatorex Street, London, E1 5NF, United Kingdom.</p>

<h2>What Are Cookies?</h2>
<p>Cookies are small text files stored on your device when you visit websites. They help websites remember your preferences and understand how you use the site.</p>

<h2>Types of Cookies We Use</h2>

<h3>Essential Cookies (Required)</h3>
<p>These cookies are necessary for the platform to function properly. They enable core features such as:</p>
<ul>
  <li>User authentication and secure login sessions</li>
  <li>Security features and fraud prevention</li>
  <li>Remembering your cookie preferences</li>
  <li>Load balancing and server routing</li>
</ul>
<p>You cannot disable essential cookies as they are required for the platform to work.</p>

<h3>Analytics Cookies (Optional)</h3>
<p>These cookies help us understand how visitors interact with OnsiteHand. They collect information such as:</p>
<ul>
  <li>Which pages you visit and how long you spend on them</li>
  <li>How you found our website</li>
  <li>Technical information about your device and browser</li>
  <li>Error reports and performance data</li>
</ul>
<p>This information is used to improve our platform and user experience. You can opt out of analytics cookies in your cookie preferences.</p>

<h3>Marketing Cookies (Optional)</h3>
<p>These cookies are used to deliver relevant advertisements and measure campaign effectiveness:</p>
<ul>
  <li>Tracking which ads you've seen</li>
  <li>Understanding campaign performance</li>
  <li>Delivering personalised content</li>
</ul>
<p>You can opt out of marketing cookies in your cookie preferences.</p>

<h2>Third-Party Cookies</h2>
<p>Some cookies are placed by third-party services that appear on our pages:</p>
<ul>
  <li><strong>Google Analytics:</strong> For website analytics (if enabled)</li>
</ul>

<h2>Managing Your Cookie Preferences</h2>
<p>You can manage your cookie preferences at any time by:</p>
<ul>
  <li>Clicking the "Cookie Preferences" link in our website footer</li>
  <li>Adjusting your browser settings to block or delete cookies</li>
</ul>
<p>Please note that blocking some cookies may impact your experience on our platform.</p>

<h2>Browser Cookie Settings</h2>
<p>Most browsers allow you to control cookies through their settings. Here are links to instructions for common browsers:</p>
<ul>
  <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer">Google Chrome</a></li>
  <li><a href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer" target="_blank" rel="noopener noreferrer">Mozilla Firefox</a></li>
  <li><a href="https://support.apple.com/en-gb/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer">Safari</a></li>
  <li><a href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer">Microsoft Edge</a></li>
</ul>

<h2>Changes to This Policy</h2>
<p>We may update this Cookie Policy from time to time. We will notify you of significant changes by updating the "Last updated" date at the top of this page.</p>

<h2>Contact Us</h2>
<p>If you have questions about our use of cookies, please contact us at:</p>
<ul>
  <li>Email: <a href="mailto:privacy@onsitehand.co.uk">privacy@onsitehand.co.uk</a></li>
</ul>
`;

export default function CookiePolicy() {
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCookiePrefs, setShowCookiePrefs] = useState(false);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/cms/pages/cookies`);
      if (response.data.sections?.main?.content) {
        setContent(response.data.sections.main.content);
      }
    } catch (error) {
      // Fallback to defaults
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="text-slate-600 hover:text-slate-900"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div 
              className="flex items-center gap-2 cursor-pointer"
              onClick={() => navigate('/')}
            >
              <Building2 className="h-6 w-6 text-orange-500" />
              <span className="text-xl font-bold text-slate-900">OnsiteHand</span>
            </div>
          </div>
          <button
            onClick={() => navigate('/login')}
            className="btn-primary text-sm"
          >
            Sign In
          </button>
        </div>
      </header>

      <main className="flex-1">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Title */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-100 rounded-full mb-4">
              <Cookie className="h-8 w-8 text-orange-500" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">Cookie Policy</h1>
            <p className="text-slate-600 max-w-2xl mx-auto">
              How we use cookies and similar technologies on OnsiteHand
            </p>
          </div>

          {/* Cookie Preferences Button */}
          <div className="text-center mb-8">
            <button
              onClick={() => setShowCookiePrefs(true)}
              className="inline-flex items-center gap-2 px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-medium"
            >
              <Cookie className="h-5 w-5" />
              Manage Cookie Preferences
            </button>
          </div>

          {/* Content */}
          <div className="card">
            {loading ? (
              <div className="animate-pulse space-y-4">
                <div className="h-4 bg-slate-200 rounded w-3/4"></div>
                <div className="h-4 bg-slate-200 rounded w-full"></div>
                <div className="h-4 bg-slate-200 rounded w-5/6"></div>
              </div>
            ) : (
              <div 
                className="prose prose-slate max-w-none prose-headings:text-slate-900 prose-a:text-orange-600 prose-li:marker:text-orange-500"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content || defaultCookieContent) }}
              />
            )}
          </div>
        </div>
      </main>

      <Footer minimal />
      <CookiePreferences open={showCookiePrefs} onClose={() => setShowCookiePrefs(false)} />
    </div>
  );
}
